import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    CContainer, CRow, CCol, CCard, CCardBody, CCardHeader, CButton, CTable,
    CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CSpinner,
    CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CForm, CFormSelect,
    CFormInput, CFormCheck, CBadge, CInputGroup, CInputGroupText
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { 
    cilArrowLeft, cilPlus, cilPencil, cilTrash, cilBell, cilSend
} from '@coreui/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

const NotificationsPage = () => {
    const { t } = useTranslation();
    const [rules, setRules] = useState([]);
    const [cameras, setCameras] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ visible: false, data: null });
    const [saving, setSaving] = useState(false);

    // Выбранный тип чата в модалке: 'bitrix' или 'telegram'
    const [chatPlatform, setChatPlatform] = useState('bitrix');

    const metrics = [
        { value: 'is_streaming_now', label: '📹 Статус камеры (0 - Офлайн, 1 - Онлайн)' },
        { value: 'people_count', label: '👥 Количество сотрудников в зоне' },
        { value: 'chair_people_count', label: '🪑 Сотрудники сидят на стуле' },
        { value: 'phone_people_count', label: '📱 Сотрудники в телефоне' },
        { value: 'queue_people_count', label: '🚶 Очередь / Клиенты' },
        { value: 'no_uniform_count', label: '👔 Сотрудник без формы' },
        { value: 'unattended_client_count', label: '⏳ Клиент без внимания (>1 мин)' },
    ];

    const operators = [
        { value: '>', label: '> (Больше)' },
        { value: '<', label: '< (Меньше)' },
        { value: '==', label: '= (Равно)' },
        { value: '!=', label: '≠ (Не равно)' }
    ];

    // Готовые шаблоны частых правил
    const presets = [
        {
            name: '🚨 Телефон на смене',
            metric: 'phone_people_count',
            operator: '>',
            threshold: 0,
            template: '⚠️ На посту {camera_name} сотрудник использует телефон!'
        },
        {
            name: '🚨 Никого нет на посту',
            metric: 'people_count',
            operator: '==',
            threshold: 0,
            template: '⚠️ На посту {camera_name} отсутствуют сотрудники!'
        },
        {
            name: '🚨 Камера отключилась',
            metric: 'is_streaming_now',
            operator: '==',
            threshold: 0,
            template: '🔴 Внимание! Пропал видеопоток с камеры {camera_name}!'
        }
    ];

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [rulesRes, camsRes] = await Promise.all([
                axios.get('/api/notification-rules/', { headers: { 'Authorization': `Bearer ${token}` } }),
                axios.get('/api/cameras/', { headers: { 'Authorization': `Bearer ${token}` } })
            ]);
            setRules(rulesRes.data.results || []);
            setCameras(camsRes.data.results || []);
        } catch (err) {
            toast.error('Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleOpenModal = (rule = null) => {
        if (rule) {
            const isBitrix = (rule.tg_chat_id || '').startsWith('chat') || /^\d+$/.test(rule.tg_chat_id || '');
            setChatPlatform(isBitrix ? 'bitrix' : 'telegram');
            setModal({ visible: true, data: { ...rule } });
        } else {
            setChatPlatform('bitrix');
            setModal({
                visible: true,
                data: {
                    name: '🚨 Нарушение регламента',
                    camera: cameras.length > 0 ? cameras[0].id : '',
                    metric: 'phone_people_count', 
                    operator: '>', 
                    threshold: 0,
                    send_telegram: true,
                    send_email: false,
                    tg_chat_id: '', 
                    email_address: '',
                    cooldown_minutes: 30, 
                    is_active: true,
                    message_template: '⚠️ На посту {camera_name} зафиксировано событие ({value})'
                }
            });
        }
    };

    const applyPreset = (preset) => {
        setModal(prev => ({
            ...prev,
            data: {
                ...prev.data,
                name: preset.name,
                metric: preset.metric,
                operator: preset.operator,
                threshold: preset.threshold,
                message_template: preset.template
            }
        }));
    };

    const handleSave = async () => {
        if (!modal.data.send_telegram && !modal.data.send_email) {
            toast.warn('Выберите хотя бы один канал отправки (Битрикс24 / Telegram или Email)');
            return;
        }

        if (modal.data.send_telegram && !modal.data.tg_chat_id.trim()) {
            toast.warn('Укажите ID чата');
            return;
        }

        if (modal.data.send_email && !modal.data.email_address.trim()) {
            toast.warn('Укажите Email адрес');
            return;
        }

        // Если выбран Битрикс24 и пользователь ввел просто цифры, добавляем префикс chat
        let formattedChatId = modal.data.tg_chat_id.trim();
        if (chatPlatform === 'bitrix' && !formattedChatId.startsWith('chat') && /^\d+$/.test(formattedChatId)) {
            formattedChatId = `chat${formattedChatId}`;
        }

        const payload = {
            ...modal.data,
            tg_chat_id: formattedChatId
        };

        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            if (modal.data.id) {
                await axios.patch(`/api/notification-rules/${modal.data.id}/`, payload, { headers: { 'Authorization': `Bearer ${token}` } });
                toast.success('Правило обновлено');
            } else {
                await axios.post('/api/notification-rules/', payload, { headers: { 'Authorization': `Bearer ${token}` } });
                toast.success('Правило создано');
            }
            setModal({ visible: false, data: null });
            fetchData();
        } catch (err) {
            toast.error('Ошибка сохранения правила');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Удалить это правило?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/notification-rules/${id}/`, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success('Удалено');
            fetchData();
        } catch (err) {
            toast.error('Ошибка удаления');
        }
    };

    return (
        <div className="bg-light min-vh-100 p-4">
            <CContainer fluid>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="m-0 d-flex align-items-center">
                            <CIcon icon={cilBell} className="me-2 text-warning" size="xl" /> 
                            Центр уведомлений и Алармов
                        </h2>
                        <p className="text-muted mb-0 small">Настройка моментальных оповещений в чаты Битрикс24, Telegram и на почту</p>
                    </div>
                    <CButton color="secondary" as={Link} to="/dashboard">
                        <CIcon icon={cilArrowLeft} className="me-2" /> {t('common.back')}
                    </CButton>
                </div>

                <CCard className="shadow-sm border-0">
                    <CCardHeader className="bg-white py-3 d-flex justify-content-between align-items-center">
                        <strong className="fs-5 text-dark">Активные правила мониторинга</strong>
                        <CButton color="primary" className="fw-bold text-white shadow-sm" onClick={() => handleOpenModal()}>
                            <CIcon icon={cilPlus} className="me-1" /> Добавить правило
                        </CButton>
                    </CCardHeader>
                    <CCardBody className="p-0">
                        {loading ? (
                            <div className="text-center py-5"><CSpinner color="primary" /></div>
                        ) : (
                            <div className="table-responsive">
                                <CTable hover align="middle" className="mb-0 text-center">
                                    <CTableHead color="light">
                                        <CTableRow>
                                            <CTableHeaderCell className="text-start ps-4">Название правила</CTableHeaderCell>
                                            <CTableHeaderCell>Пост / Камера</CTableHeaderCell>
                                            <CTableHeaderCell>Условие сработки</CTableHeaderCell>
                                            <CTableHeaderCell>Канал доставки</CTableHeaderCell>
                                            <CTableHeaderCell>Кулдаун</CTableHeaderCell>
                                            <CTableHeaderCell>Статус</CTableHeaderCell>
                                            <CTableHeaderCell>Последняя отправка</CTableHeaderCell>
                                            <CTableHeaderCell className="text-end pe-4">Действия</CTableHeaderCell>
                                        </CTableRow>
                                    </CTableHead>
                                    <CTableBody>
                                        {rules.length === 0 ? (
                                            <CTableRow>
                                                <CTableDataCell colSpan="8" className="text-center text-muted py-5">
                                                    У вас пока нет настроенных правил. Нажмите <strong>«Добавить правило»</strong>, чтобы подключить чат Битрикс24 или Telegram.
                                                </CTableDataCell>
                                            </CTableRow>
                                        ) : rules.map(rule => {
                                            const isB24 = (rule.tg_chat_id || '').startsWith('chat');
                                            return (
                                                <CTableRow key={rule.id}>
                                                    <CTableDataCell className="text-start ps-4 fw-bold text-dark">
                                                        {rule.name}
                                                    </CTableDataCell>
                                                    <CTableDataCell>
                                                        <span className="badge bg-light text-dark border">{rule.camera_name}</span>
                                                    </CTableDataCell>
                                                    <CTableDataCell>
                                                        <code>{rule.metric}</code> <strong>{rule.operator} {rule.threshold}</strong>
                                                    </CTableDataCell>
                                                    <CTableDataCell>
                                                        <div className="d-flex justify-content-center gap-1">
                                                            {rule.send_telegram && (
                                                                <CBadge color={isB24 ? 'info' : 'primary'} className="p-1 px-2">
                                                                    {isB24 ? '🏢 Bitrix24' : '✈️ Telegram'}
                                                                </CBadge>
                                                            )}
                                                            {rule.send_email && (
                                                                <CBadge color="warning" className="p-1 px-2 text-dark">✉️ Email</CBadge>
                                                            )}
                                                        </div>
                                                        {rule.tg_chat_id && <small className="text-muted d-block mt-1">{rule.tg_chat_id}</small>}
                                                    </CTableDataCell>
                                                    <CTableDataCell>{rule.cooldown_minutes} мин.</CTableDataCell>
                                                    <CTableDataCell>
                                                        <CBadge color={rule.is_active ? 'success' : 'secondary'}>
                                                            {rule.is_active ? '● Активно' : '○ Отключено'}
                                                        </CBadge>
                                                    </CTableDataCell>
                                                    <CTableDataCell className="text-muted small">
                                                        {rule.last_triggered_at ? dayjs(rule.last_triggered_at).format('DD.MM.YYYY HH:mm') : '—'}
                                                    </CTableDataCell>
                                                    <CTableDataCell className="text-end pe-4">
                                                        <CButton color="light" size="sm" className="me-2" onClick={() => handleOpenModal(rule)}>
                                                            <CIcon icon={cilPencil} />
                                                        </CButton>
                                                        <CButton color="danger" variant="ghost" size="sm" onClick={() => handleDelete(rule.id)}>
                                                            <CIcon icon={cilTrash} />
                                                        </CButton>
                                                    </CTableDataCell>
                                                </CTableRow>
                                            );
                                        })}
                                    </CTableBody>
                                </CTable>
                            </div>
                        )}
                    </CCardBody>
                </CCard>
            </CContainer>

            {/* ========================================================================= */}
            {/* КРАСИВОЕ МОДАЛЬНОЕ ОКНО СОЗДАНИЯ / РЕДАКТИРОВАНИЯ ПРАВИЛА */}
            {/* ========================================================================= */}
            <CModal size="lg" alignment="center" visible={modal.visible} onClose={() => setModal({ visible: false, data: null })}>
                <CModalHeader className="bg-light">
                    <CModalTitle className="d-flex align-items-center text-primary fw-bold">
                        <CIcon icon={cilBell} className="me-2" />
                        {modal.data?.id ? 'Редактирование правила оповещения' : 'Создание правила оповещения'}
                    </CModalTitle>
                </CModalHeader>

                <CModalBody className="p-4">
                    {modal.data && (
                        <CForm>
                            {/* БЫСТРЫЕ ШАБЛОНЫ */}
                            {!modal.data.id && (
                                <div className="mb-4 p-3 bg-light rounded border">
                                    <div className="small fw-bold text-muted mb-2 text-uppercase">⚡ Быстрые шаблоны правил:</div>
                                    <div className="d-flex gap-2 flex-wrap">
                                        {presets.map((p, idx) => (
                                            <CButton 
                                                key={idx} 
                                                color="secondary" 
                                                variant="outline" 
                                                size="sm" 
                                                className="bg-white text-dark shadow-sm"
                                                onClick={() => applyPreset(p)}
                                            >
                                                {p.name}
                                            </CButton>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ОСНОВНЫЕ ПОЛЯ */}
                            <CRow className="g-3 mb-3">
                                <CCol md={6}>
                                    <label className="form-label fw-bold">Название правила:</label>
                                    <CFormInput
                                        placeholder="Например: 🚨 Нарушение: Телефон на кассе"
                                        value={modal.data.name}
                                        onChange={e => setModal({ ...modal, data: { ...modal.data, name: e.target.value } })}
                                    />
                                </CCol>

                                <CCol md={6}>
                                    <label className="form-label fw-bold">Контролируемый пост / камера:</label>
                                    <CFormSelect 
                                        value={modal.data.camera} 
                                        onChange={e => setModal({ ...modal, data: { ...modal.data, camera: e.target.value } })}
                                    >
                                        {cameras.map(c => <option key={c.id} value={c.id}>📹 {c.name}</option>)}
                                    </CFormSelect>
                                </CCol>
                            </CRow>

                            {/* УСЛОВИЕ СРАБАТЫВАНИЯ */}
                            <div className="p-3 mb-4 rounded border bg-white shadow-sm">
                                <label className="form-label fw-bold text-primary mb-2">🎯 Условие сработки правила:</label>
                                <CRow className="g-2 align-items-center">
                                    <CCol md={6}>
                                        <CFormSelect 
                                            value={modal.data.metric} 
                                            onChange={e => setModal({ ...modal, data: { ...modal.data, metric: e.target.value } })}
                                        >
                                            {metrics.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                        </CFormSelect>
                                    </CCol>

                                    <CCol md={3}>
                                        <CFormSelect 
                                            value={modal.data.operator} 
                                            onChange={e => setModal({ ...modal, data: { ...modal.data, operator: e.target.value } })}
                                        >
                                            {operators.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </CFormSelect>
                                    </CCol>

                                    <CCol md={3}>
                                        <CInputGroup>
                                            <CFormInput 
                                                type="number" 
                                                value={modal.data.threshold} 
                                                onChange={e => setModal({ ...modal, data: { ...modal.data, threshold: e.target.value } })} 
                                            />
                                            <CInputGroupText>чел/ед</CInputGroupText>
                                        </CInputGroup>
                                    </CCol>
                                </CRow>
                            </div>

                            {/* КАНАЛЫ ДОСТАВКИ: BITRIX24 / TELEGRAM / EMAIL */}
                            <div className="p-3 mb-4 rounded border bg-light">
                                <label className="form-label fw-bold d-block mb-3">
                                    <CIcon icon={cilSend} className="me-2 text-success" />
                                    Каналы отправки уведомления:
                                </label>

                                <div className="d-flex gap-4 mb-3">
                                    <CFormCheck 
                                        type="radio"
                                        name="channel_type"
                                        id="channel_b24"
                                        label={<strong>🏢 Чат Битрикс24</strong>}
                                        checked={modal.data.send_telegram && chatPlatform === 'bitrix'}
                                        onChange={() => {
                                            setChatPlatform('bitrix');
                                            setModal({ ...modal, data: { ...modal.data, send_telegram: true } });
                                        }}
                                    />
                                    <CFormCheck 
                                        type="radio"
                                        name="channel_type"
                                        id="channel_tg"
                                        label={<strong>✈️ Чат Telegram</strong>}
                                        checked={modal.data.send_telegram && chatPlatform === 'telegram'}
                                        onChange={() => {
                                            setChatPlatform('telegram');
                                            setModal({ ...modal, data: { ...modal.data, send_telegram: true } });
                                        }}
                                    />
                                    <CFormCheck 
                                        type="checkbox"
                                        id="channel_email"
                                        label="✉️ Дублировать на Email"
                                        checked={modal.data.send_email}
                                        onChange={e => setModal({ ...modal, data: { ...modal.data, send_email: e.target.checked } })}
                                    />
                                </div>

                                {/* ПОЛЕ ДЛЯ БИТРИКС24 */}
                                {modal.data.send_telegram && chatPlatform === 'bitrix' && (
                                    <div className="p-3 bg-white border rounded mb-3">
                                        <label className="form-label small fw-bold text-dark">
                                            ID чата Битрикс24 (куда отправлять фото и видео-ссылку):
                                        </label>
                                        <CInputGroup>
                                            <CInputGroupText className="bg-info text-white fw-bold">Bitrix24</CInputGroupText>
                                            <CFormInput 
                                                placeholder="Например: chat142" 
                                                value={modal.data.tg_chat_id}
                                                onChange={e => setModal({ ...modal, data: { ...modal.data, tg_chat_id: e.target.value } })}
                                            />
                                        </CInputGroup>
                                        <div className="small text-muted mt-2">
                                            💡 <strong>Где взять ID:</strong> Откройте нужный групповой чат в Битрикс24 через браузер. В ссылке страницы будет указан параметр <code>IM_DIALOG=chat142</code>. Вставьте <code>chat142</code> сюда.
                                        </div>
                                    </div>
                                )}

                                {/* ПОЛЕ ДЛЯ TELEGRAM */}
                                {modal.data.send_telegram && chatPlatform === 'telegram' && (
                                    <div className="p-3 bg-white border rounded mb-3">
                                        <label className="form-label small fw-bold text-dark">
                                            Telegram Chat ID:
                                        </label>
                                        <CInputGroup>
                                            <CInputGroupText className="bg-primary text-white fw-bold">Telegram</CInputGroupText>
                                            <CFormInput 
                                                placeholder="Например: -1002697374867" 
                                                value={modal.data.tg_chat_id}
                                                onChange={e => setModal({ ...modal, data: { ...modal.data, tg_chat_id: e.target.value } })}
                                            />
                                        </CInputGroup>
                                        <div className="small text-muted mt-2">
                                            🤖 Запустите бота <a href="https://t.me/neimarker_alert_bot" target="_blank" rel="noopener noreferrer" className="fw-bold">@neimarker_alert_bot</a> перед подключением.
                                        </div>
                                    </div>
                                )}

                                {/* ПОЛЕ ДЛЯ EMAIL */}
                                {modal.data.send_email && (
                                    <div className="p-3 bg-white border rounded">
                                        <label className="form-label small fw-bold text-dark">Email адрес для отчета:</label>
                                        <CFormInput 
                                            type="email"
                                            placeholder="manager@moscvetok.ru"
                                            value={modal.data.email_address}
                                            onChange={e => setModal({ ...modal, data: { ...modal.data, email_address: e.target.value } })}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* ЗАДЕРЖКА И ШАБЛОН СООБЩЕНИЯ */}
                            <CRow className="g-3 mb-3">
                                <CCol md={6}>
                                    <label className="form-label fw-bold">Задержка повторной сработки (минут):</label>
                                    <CInputGroup>
                                        <CFormInput 
                                            type="number" 
                                            value={modal.data.cooldown_minutes} 
                                            onChange={e => setModal({ ...modal, data: { ...modal.data, cooldown_minutes: e.target.value } })} 
                                        />
                                        <CInputGroupText>мин.</CInputGroupText>
                                    </CInputGroup>
                                    <small className="text-muted">Чтобы чат не спамило каждую секунду одного нарушения.</small>
                                </CCol>

                                <CCol md={6} className="d-flex align-items-center">
                                    <CFormCheck 
                                        id="ruleActiveCheck"
                                        label={<strong>Включить правило сразу после сохранения</strong>} 
                                        checked={modal.data.is_active} 
                                        onChange={e => setModal({ ...modal, data: { ...modal.data, is_active: e.target.checked } })} 
                                    />
                                </CCol>
                            </CRow>

                            <div className="mb-2">
                                <label className="form-label fw-bold">Текст уведомления:</label>
                                <textarea
                                    className="form-control"
                                    rows="2"
                                    value={modal.data.message_template}
                                    onChange={e => setModal({ ...modal, data: { ...modal.data, message_template: e.target.value } })}
                                ></textarea>
                                <small className="text-muted">Поддерживаются теги: <code>{'{camera_name}'}</code>, <code>{'{value}'}</code></small>
                            </div>
                        </CForm>
                    )}
                </CModalBody>

                <CModalFooter className="bg-light">
                    <CButton color="secondary" onClick={() => setModal({ visible: false, data: null })}>
                        Отмена
                    </CButton>
                    <CButton color="success" className="text-white fw-bold px-4 shadow-sm" onClick={handleSave} disabled={saving}>
                        {saving ? <CSpinner size="sm" /> : 'Сохранить правило'}
                    </CButton>
                </CModalFooter>
            </CModal>
        </div>
    );
};

export default NotificationsPage;